import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Save, X, Plus, Trash2, FileSignature, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Settings → Estimate Templates editor. Per-roof-type templates power the
 * legacy Access-parity "Estimate Repair Overview" and "Detailed Roof
 * Appraisal" PDFs. Each template stores the title, intro paragraphs,
 * repair-options bullets, ridging info, restoration description, vent
 * note, discount note, and an estimate price list with default $ amounts.
 *
 * Backend: GET /estimate-templates, GET/PUT /estimate-templates/{roof_key}.
 */

const TEXT_FIELDS = [
  { key: 'title', label: 'Overview title', help: '"Old Concrete Tile Repairs" — printed on the Estimate Overview PDF.', rows: 1 },
  { key: 'appraisal_title', label: 'Detailed appraisal title', help: '"Old Concrete Roof Appraisal" — printed on the Detailed Roof Appraisal PDF.', rows: 1 },
  { key: 'intro_overview', label: 'Estimate Overview intro paragraph', help: 'The opening paragraph before the roof details on the free-estimate PDF. Supports {property_address}, {client_name}, {roof_type}.', rows: 4 },
  { key: 'intro_appraisal', label: 'Detailed Appraisal intro paragraph', help: 'The opening paragraph on the detailed inspection PDF.', rows: 4 },
  { key: 'main_repair_options_label', label: 'Repair options heading', help: '"Main repair options for X roofs:" — section heading above the bullet list.', rows: 1 },
  { key: 'ridging_info', label: 'Roofing information paragraph', help: 'Long-form ridging / roof anatomy text on the Detailed Appraisal PDF. Separate paragraphs with a blank line.', rows: 8 },
  { key: 'restoration_description', label: 'Restoration description', help: 'Short sentence describing the restoration process for this roof type.', rows: 3 },
  { key: 'discount_note', label: 'Discount / closing note', help: 'Closing pitch about combined-work discounts.', rows: 3 },
  { key: 'vent_note', label: 'Vent note (italics)', help: 'Optional italicised note about included spin-away vents. Leave blank to skip.', rows: 2 },
];

export default function EstimateTemplatesPage() {
  const { api } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/estimate-templates');
      setTemplates(Array.isArray(data) ? data : []);
    } catch { setTemplates([]); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const startEdit = (t) => setEditing({
    ...t,
    main_repair_options: [...(t.main_repair_options || [])],
    estimate_rows: (t.estimate_rows || []).map(r => ({ ...r })),
    match_terms: [...(t.match_terms || [])],
  });

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const { _id, ...payload } = editing;
      const variant = editing.variant || 'detailed_single';
      await api.put(`/estimate-templates/${editing.roof_key}?variant=${encodeURIComponent(variant)}`, payload);
      setEditing(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const updateField = (key, val) => setEditing(prev => ({ ...prev, [key]: val }));
  const updateBullet = (i, val) => {
    const arr = [...editing.main_repair_options];
    arr[i] = val;
    setEditing(prev => ({ ...prev, main_repair_options: arr }));
  };
  const addBullet = () => setEditing(prev => ({ ...prev, main_repair_options: [...prev.main_repair_options, ''] }));
  const removeBullet = (i) => setEditing(prev => ({ ...prev, main_repair_options: prev.main_repair_options.filter((_, idx) => idx !== i) }));

  const updateRow = (i, field, val) => {
    const arr = [...editing.estimate_rows];
    let coerced = val;
    if (field === 'default' || field === 'rate') {
      coerced = val === '' || val === null ? null : (parseFloat(val) || 0);
    }
    arr[i] = { ...arr[i], [field]: coerced };
    setEditing(prev => ({ ...prev, estimate_rows: arr }));
  };
  const addRow = () => setEditing(prev => ({ ...prev, estimate_rows: [...prev.estimate_rows, { label: '', key: '', rate: null, unit: 'flat', quantity_source: 'flat', default: 0 }] }));
  const removeRow = (i) => setEditing(prev => ({ ...prev, estimate_rows: prev.estimate_rows.filter((_, idx) => idx !== i) }));

  return (
    <div data-testid="estimate-templates-page" className="space-y-5">
      <div className="bg-white border border-[#E2E2DF] rounded-md p-6 relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-[3px] bg-[#316536]" />
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-md bg-gradient-to-br from-[#316536]/15 to-[#316536]/5 flex items-center justify-center ring-1 ring-[#316536]/10">
            <FileSignature size={22} className="text-[#316536]" strokeWidth={2} />
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-0.5">Settings</p>
            <h1 className="text-2xl font-extrabold text-slate-900 font-[Manrope] tracking-tight">Estimate Templates</h1>
            <p className="text-sm text-slate-500 mt-1">{templates.length} per-roof-type template{templates.length === 1 ? '' : 's'} — drives the <span className="font-mono-num text-slate-700">Estimate Overview</span> + <span className="font-mono-num text-slate-700">Detailed Roof Appraisal</span> PDFs.</p>
          </div>
        </div>
      </div>

      {templates.length === 0 && (
        <div className="bg-white border border-[#E2E2DF] rounded-xl p-12 text-center text-sm text-slate-500">
          No templates seeded yet. Run <code className="font-mono-num text-slate-700">python backend/seed_estimate_templates.py</code> to bootstrap.
        </div>
      )}

      <div className="grid grid-cols-1 gap-3">
        {templates.map(t => (
          <div key={`${t.roof_key}-${t.variant || 'legacy'}`} data-testid={`estimate-template-row-${t.roof_key}-${t.variant || 'legacy'}`} className="bg-white border border-[#E2E2DF] rounded-xl overflow-hidden">
          {(() => { const rowId = `${t.roof_key}-${t.variant || 'legacy'}`; const isOpen = !!expanded[rowId]; return (
          <>
          <div
            role="button"
            tabIndex={0}
            onClick={() => setExpanded(prev => ({ ...prev, [rowId]: !prev[rowId] }))}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(prev => ({ ...prev, [rowId]: !prev[rowId] })); } }}
            className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50/50 transition-colors text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#316536]/30"
          >
            <div className="flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#316536] bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5">{t.roof_key}</span>
              {t.variant && (
                <span className={`text-[10px] uppercase tracking-[0.16em] font-bold rounded-full px-2 py-0.5 ${
                  t.variant === 'instant_free' ? 'bg-slate-100 text-slate-700 border border-slate-300' :
                  t.variant === 'detailed_single' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                  'bg-purple-50 text-purple-700 border border-purple-200'
                }`}>
                  {t.variant === 'instant_free' ? 'Instant Free' :
                   t.variant === 'detailed_single' ? 'Detailed 1-Comment' : 'Detailed Multi-Defect'}
                </span>
              )}
              <h2 className="text-sm font-bold text-slate-800">{t.title}</h2>
              <span className="text-[11px] text-slate-400">· {(t.estimate_rows || []).length} estimate rows · {(t.main_repair_options || []).length} bullets</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={(e) => { e.stopPropagation(); startEdit(t); }} data-testid={`edit-template-${t.roof_key}-${t.variant || 'legacy'}`} className="px-3 py-1.5 bg-[#316536] text-white rounded-md text-xs font-semibold hover:bg-[#234725] transition-colors">Edit</button>
              {isOpen ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
            </div>
          </div>
            {isOpen && (
              <div className="px-5 pb-5 pt-1 space-y-3 border-t border-[#F0EFEC]">
                <Field label="Match terms" value={(t.match_terms || []).join(', ')} />
                <Field label="Overview intro" value={t.intro_overview} multiline />
                <Field label="Main repair options" value={(t.main_repair_options || []).map(o => `• ${o}`).join('\n')} multiline />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Discount note" value={t.discount_note} multiline />
                  <Field label="Restoration description" value={t.restoration_description} multiline />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-1">Estimate rows</p>
                  <div className="grid grid-cols-1 gap-2">
                    {(t.estimate_rows || []).map((r, i) => {
                      const unit = r.unit || 'flat';
                      const rate = r.rate;
                      const qty = r.quantity_source || 'flat';
                      const isRate = rate != null && qty && qty !== 'flat';
                      const unitSfx = unit === 'm2' ? '/m²' : (unit === 'ridge_m' ? '/m' : '');
                      const qtyLbl = qty === 'roof_m2' ? '× roof m²' : (qty === 'ridge_length_m' ? '× ridge length' : 'flat');
                      return (
                        <div key={i} data-testid={`estimate-row-${t.roof_key}-${r.key}`} className="text-xs px-2.5 py-1.5 bg-slate-50 border border-[#E2E2DF] rounded-md flex items-center justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="text-slate-800 font-semibold">{r.label} <span className="text-[10px] font-mono-num text-slate-400">({r.key})</span></div>
                            <div className="text-[10px] text-slate-500 mt-0.5">
                              {isRate
                                ? <span><span className="font-mono-num text-[#316536] font-bold">${rate}{unitSfx}</span> <span className="text-slate-400">{qtyLbl}</span></span>
                                : <span className="italic text-slate-400">flat / manual $</span>}
                            </div>
                          </div>
                          {(r.default != null && r.default !== 0 && !isRate) && (
                            <span className="text-[#D35400] font-bold font-mono-num text-xs">${Number(r.default || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </>
          ); })()}
          </div>
        ))}
      </div>

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div data-testid="estimate-template-editor" className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">Editing</p>
                <h2 className="text-base font-bold text-[#0F172A]">{editing.title} <span className="text-xs text-slate-400 font-mono-num font-normal">({editing.roof_key})</span></h2>
              </div>
              <button onClick={() => setEditing(null)} className="p-1.5 hover:bg-slate-100 rounded-md text-slate-500"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              {TEXT_FIELDS.map(f => (
                <div key={f.key}>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">{f.label}</label>
                  {f.rows > 1 ? (
                    <textarea data-testid={`template-${f.key}`} value={editing[f.key] || ''} onChange={e => updateField(f.key, e.target.value)} rows={f.rows} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm leading-relaxed focus:outline-none focus:border-[#316536]" />
                  ) : (
                    <input data-testid={`template-${f.key}`} value={editing[f.key] || ''} onChange={e => updateField(f.key, e.target.value)} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm focus:outline-none focus:border-[#316536]" />
                  )}
                  <p className="text-[11px] text-slate-400 mt-0.5">{f.help}</p>
                </div>
              ))}

              {/* Bullet list editor */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-slate-700">Main repair options (bullets)</label>
                  <button data-testid="add-bullet-btn" onClick={addBullet} className="text-[11px] text-[#316536] hover:text-[#234725] font-semibold flex items-center gap-1"><Plus size={11} /> Add bullet</button>
                </div>
                <div className="space-y-1.5">
                  {editing.main_repair_options.map((opt, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <span className="text-slate-400 text-xs">•</span>
                      <input data-testid={`bullet-${i}`} value={opt} onChange={e => updateBullet(i, e.target.value)} className="flex-1 px-2.5 py-1.5 border border-[#E2E2DF] rounded-md text-sm" />
                      <button data-testid={`remove-bullet-${i}`} onClick={() => removeBullet(i)} className="p-1.5 text-slate-400 hover:text-rose-500"><Trash2 size={13} /></button>
                    </div>
                  ))}
                  {editing.main_repair_options.length === 0 && <p className="text-xs text-slate-400 italic">No bullets yet — click &ldquo;Add bullet&rdquo; above.</p>}
                </div>
              </div>

              {/* Estimate rows editor */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-slate-700">Estimate price rows</label>
                  <button data-testid="add-row-btn" onClick={addRow} className="text-[11px] text-[#316536] hover:text-[#234725] font-semibold flex items-center gap-1"><Plus size={11} /> Add row</button>
                </div>
                <div className="space-y-1.5">
                  <div className="grid grid-cols-24 gap-1.5 px-1 text-[10px] uppercase tracking-[0.14em] font-bold text-slate-400" style={{gridTemplateColumns:'4fr 2fr 1.6fr 1.4fr 2fr 1.6fr 0.4fr'}}>
                    <span>Label</span>
                    <span>Key</span>
                    <span>Rate</span>
                    <span>Unit</span>
                    <span>Qty source</span>
                    <span>Flat $</span>
                    <span></span>
                  </div>
                  {editing.estimate_rows.map((r, i) => (
                    <div key={i} className="grid gap-1.5 items-center" style={{gridTemplateColumns:'4fr 2fr 1.6fr 1.4fr 2fr 1.6fr 0.4fr'}}>
                      <input data-testid={`row-label-${i}`} value={r.label} onChange={e => updateRow(i, 'label', e.target.value)} placeholder="ReScrew Estimation" className="px-2 py-1.5 border border-[#E2E2DF] rounded-md text-xs" />
                      <input data-testid={`row-key-${i}`} value={r.key} onChange={e => updateRow(i, 'key', e.target.value)} placeholder="rescrew" className="px-2 py-1.5 border border-[#E2E2DF] rounded-md text-xs font-mono-num" />
                      <input data-testid={`row-rate-${i}`} type="number" step="0.01" value={r.rate ?? ''} placeholder="13.00" onChange={e => updateRow(i, 'rate', e.target.value)} className="px-2 py-1.5 border border-[#E2E2DF] rounded-md text-xs font-mono-num text-right" />
                      <select data-testid={`row-unit-${i}`} value={r.unit || 'flat'} onChange={e => updateRow(i, 'unit', e.target.value)} className="px-1.5 py-1.5 border border-[#E2E2DF] rounded-md text-xs bg-white">
                        <option value="m2">$/m²</option>
                        <option value="ridge_m">$/m ridge</option>
                        <option value="flat">flat $</option>
                      </select>
                      <select data-testid={`row-qsrc-${i}`} value={r.quantity_source || 'flat'} onChange={e => updateRow(i, 'quantity_source', e.target.value)} className="px-1.5 py-1.5 border border-[#E2E2DF] rounded-md text-xs bg-white">
                        <option value="roof_m2">Roof m²</option>
                        <option value="ridge_length_m">Ridge length</option>
                        <option value="flat">Flat (manual)</option>
                      </select>
                      <input data-testid={`row-default-${i}`} type="number" step="0.01" value={r.default ?? ''} placeholder="0" onChange={e => updateRow(i, 'default', e.target.value)} className="px-2 py-1.5 border border-[#E2E2DF] rounded-md text-xs font-mono-num text-right" />
                      <button data-testid={`remove-row-${i}`} onClick={() => removeRow(i)} className="p-1 text-slate-400 hover:text-rose-500 justify-self-end"><Trash2 size={13} /></button>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-slate-400 mt-1.5">
                  Row calc: <b>Rate × Qty source</b> at PDF time (e.g. <code className="font-mono-num">$13/m² × Roof m²</code>).
                  Set Unit + Qty source to <code className="font-mono-num">flat</code> for manual $ entry (extra_work).
                  The <b>reroof</b> row uses the Reroof Rate Card matrix — leave Rate blank; qty source stays <code className="font-mono-num">Roof m²</code>.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-slate-200 bg-slate-50/50">
              <button onClick={() => setEditing(null)} className="px-4 py-2 border border-[#E2E2DF] rounded-lg text-sm text-slate-600 hover:bg-white font-medium">Cancel</button>
              <button data-testid="save-template-btn" onClick={save} disabled={saving} className="px-4 py-2 bg-[#316536] text-white rounded-lg text-sm font-semibold hover:bg-[#234725] disabled:opacity-50 flex items-center gap-1.5"><Save size={14} /> {saving ? 'Saving…' : 'Save template'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, multiline }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-1">{label}</p>
      {multiline ? (
        <pre className="text-xs text-slate-700 whitespace-pre-wrap font-sans bg-slate-50/60 border border-[#E2E2DF] rounded-md p-2.5 leading-relaxed">{value || <span className="text-slate-400 italic">(empty)</span>}</pre>
      ) : (
        <p className="text-xs text-slate-700 font-mono-num bg-slate-50/60 border border-[#E2E2DF] rounded-md px-2.5 py-1.5">{value || <span className="text-slate-400 italic">(empty)</span>}</p>
      )}
    </div>
  );
}
